/**
 * The Quick Tour button.
 *
 * 🔴 Uses plain markup and inline styles rather than the app's `Button` /
 * `Tooltip` primitives. That is the whole point of the module boundary: the
 * tour guide can be lifted into another codebase, or survive a design-system
 * swap, without a single import to chase. It is one small button — the cost of
 * not sharing the primitive is a few lines of CSS.
 */

import React from 'react';

import { PRODUCT_TOUR } from '../tours';
import { useTourGuide } from '../TourGuideProvider';

export function TourLauncher({ tourId = PRODUCT_TOUR.id }: { tourId?: string }) {
  const tour = useTourGuide();

  // No provider (or a disabled module) means no button — never a crash.
  if (!tour) return null;

  const completed = Boolean(tour.progress[tourId]);
  const running = tour.run.running;

  return (
    <button
      type="button"
      /*
       * 🔴 THE `--tour` MODIFIER EXISTS ONLY SO THE ATTENTION WAVE CAN FIND THIS
       * BUTTON. Plans and Demo already carry `--plans` / `--demo`, so the tour
       * launcher was the one member of the group with no selector of its own — and
       * the wave has to ripple out of THIS pill and not out of all three (see
       * `.tour-guide-launcher--tour` in `tour-guide.css`). It intentionally carries
       * no appearance of its own; the shared look stays on the base class.
       */
      className="tour-guide-launcher tour-guide-launcher--tour"
      data-testid="quick-tour-button"
      data-completed={completed ? '' : undefined}
      aria-label={completed ? 'Replay the guided product tour' : 'Start the guided product tour'}
      /*
       * 🔴 NO `title`. When the header has no room for labels the module turns the
       * label span itself into a tooltip (see `tour-guide.css`), so a `title` would
       * pop a SECOND, slower tooltip saying almost the same thing a second later —
       * which reads as a bug. `aria-label` above still carries the full sentence for
       * assistive tech, and it is the one place that varies with `completed`.
       */
      disabled={running}
      onClick={() => tour.start(tourId)}
    >
      {/* Inlined so the module carries no icon-library dependency. */}
      <svg
        className="tour-guide-launcher__icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
      </svg>
      <span className="tour-guide-launcher__label">Quick Tour</span>
    </button>
  );
}
